import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  Bookmark,
  ChevronLeft,
  ChevronRight,
  Eye,
  Heart,
  MessageCircle,
  Minus,
  MoreHorizontal,
  Plus,
  RotateCcw,
  Send,
  X,
} from "lucide-react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import PosterArtwork from "../../entities/post/ui/PosterArtwork";
import {
  createPostComment,
  getCursorFromPageUrl,
  getPost,
  listPostComments,
  setPostCollection,
  setPostLike,
} from "../../entities/post/api";
import { getApiErrorMessage } from "../../shared/api/errors";
import {
  invalidatePostQueries,
  restorePostCaches,
  updatePostCaches,
} from "../../features/post-engagement/postCache";
import { adaptPostDetail } from "../../entities/post/adapter";
import { IS_API_MODE } from "../../shared/config/runtime";
import { initialPosts } from "../../shared/data/mockData";
import { useAuthStore } from "../../features/auth/store";
import { getFollowingUsers, getUser, setUserFollow } from "../../entities/user/api";
import { deletePost, hidePost } from "../../entities/post/api";
import defaultAvatar from "../../assets/default-avatar.png";


const mockComments = [
  {
    id: "mock-1",
    user_id: "21",
    content: "字体层级和色彩对比处理得很漂亮，主标题的节奏感很强。",
    created_at: "2026-07-28T15:20:00+08:00",
  },
  {
    id: "mock-2",
    user_id: "34",
    content: "很喜欢这套网格系统，信息量大但阅读路径依然清晰。",
    created_at: "2026-07-28T15:42:00+08:00",
  },
];

function formatCommentTime(value) {
  if (!value) return "刚刚";
  return new Intl.DateTimeFormat("zh-CN", {
    month: "numeric",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(value));
}

export default function PostDetailPage({ modal = false }) {
  const { postId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useAuthStore((state) => state.user);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const followingQuery = useQuery({
    queryKey: ["users", user?.id, "following"],
    queryFn: () => getFollowingUsers(user.id),
    enabled: IS_API_MODE && Boolean(user?.id),
    staleTime: 60_000,
  });
  const [following, setFollowing] = useState(false);
  const [localComments, setLocalComments] = useState(IS_API_MODE ? [] : mockComments);
  const [commentUsers, setCommentUsers] = useState({});
  const [comment, setComment] = useState("");
  const [replyTarget, setReplyTarget] = useState(null);
  const [expandedReplies, setExpandedReplies] = useState({});
  const [ownerMenuOpen, setOwnerMenuOpen] = useState(false);
  const [visibilityStatus, setVisibilityStatus] = useState("公开");
  const [submittingComment, setSubmittingComment] = useState(false);
  const [actionError, setActionError] = useState("");
  const [optimisticState, setOptimisticState] = useState(null);
  const [activeMediaIndex, setActiveMediaIndex] = useState(0);
  const [mediaViewerOpen, setMediaViewerOpen] = useState(false);
  const [mediaZoom, setMediaZoom] = useState(1);
  const [mediaOffset, setMediaOffset] = useState({ x: 0, y: 0 });
  const [mediaDragging, setMediaDragging] = useState(false);
  const commentInputRef = useRef(null);
  const mediaSwipeStartRef = useRef(null);
  const mediaDragRef = useRef(null);
  const mediaViewerOpenRef = useRef(mediaViewerOpen);
  mediaViewerOpenRef.current = mediaViewerOpen;
  const backgroundLocation = location.state?.backgroundLocation;

  const closeDetail = useCallback(() => {
    if (modal && backgroundLocation?.pathname) {
      navigate(
        {
          pathname: backgroundLocation.pathname,
          search: backgroundLocation.search || "",
          hash: backgroundLocation.hash || "",
        },
        { replace: true, state: backgroundLocation.state },
      );
      return;
    }
    navigate("/explore", { replace: true });
  }, [backgroundLocation, modal, navigate]);

  useEffect(() => {
    if (location.state?.focusComments) commentInputRef.current?.focus();
  }, [location.state]);

  useEffect(() => {
    if (!modal) return undefined;
    const closeOnEscape = (event) => {
      if (event.key === "Escape" && !mediaViewerOpenRef.current) closeDetail();
    };
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [closeDetail, modal]);

  useEffect(() => {
    if (!modal && !mediaViewerOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [mediaViewerOpen, modal]);

  const postQuery = useQuery({
    queryKey: ["posts", "detail", postId],
    queryFn: () => getPost(postId),
    enabled: IS_API_MODE,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const commentsQuery = useInfiniteQuery({
    queryKey: ["posts", postId, "comments"],
    queryFn: ({ pageParam }) => listPostComments(postId, pageParam),
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => getCursorFromPageUrl(lastPage?.next),
    enabled: IS_API_MODE,
  });

  const post = useMemo(() => {
    if (IS_API_MODE) {
      return postQuery.data ? adaptPostDetail(postQuery.data) : null;
    }
    const mockPost = initialPosts.find((item) => item.id === postId) || initialPosts[0];
    return {
      ...mockPost,
      content: mockPost.caption,
      mediaItems: [],
      topics: ["平面设计"],
      views: 4621,
    };
  }, [postId, postQuery.data]);

  const comments = useMemo(() => {
    const remoteComments =
      commentsQuery.data?.pages.flatMap((page) => page?.results || []) || [];
    return [...remoteComments, ...localComments];
  }, [commentsQuery.data, localComments]);

  useEffect(() => {
    if (!IS_API_MODE || !comments.length) return;
    const ids = [...new Set(comments.map((item) => item.user_id).filter(Boolean))];
    Promise.all(ids.filter((id) => !commentUsers[id]).map(async (id) => [id, await getUser(id)]))
      .then((entries) => {
        if (entries.length) setCommentUsers((current) => ({ ...current, ...Object.fromEntries(entries) }));
      })
      .catch(() => {});
  }, [comments, commentUsers]);

  const visiblePost = useMemo(
    () => (optimisticState && post ? { ...post, ...optimisticState } : post),
    [optimisticState, post],
  );
  const detailMediaItems = useMemo(() => {
    if (!visiblePost) return [];
    const sortedItems = [...(visiblePost.mediaItems || [])]
      .filter((item) => !item.media_type || item.media_type === "image")
      .sort((a, b) => Number(a.sort_order || 0) - Number(b.sort_order || 0));
    const seenUrls = new Set();
    const items = sortedItems
      .map((item) => ({
        id: item.id || item.file_key || item.file_url,
        url: item.file_url || item.fileUrl || "",
      }))
      .filter((item) => {
        if (!item.url || seenUrls.has(item.url)) return false;
        seenUrls.add(item.url);
        return true;
      });
    if (!items.length && visiblePost.image) {
      return [{ id: "cover", url: visiblePost.image }];
    }
    return items;
  }, [visiblePost]);
  const activeMedia = detailMediaItems[activeMediaIndex] || detailMediaItems[0] || null;
  const hasMultipleMedia = detailMediaItems.length > 1;

  useEffect(() => {
    setActiveMediaIndex(0);
  }, [postId]);

  useEffect(() => {
    setActiveMediaIndex((current) => Math.min(current, Math.max(detailMediaItems.length - 1, 0)));
  }, [detailMediaItems.length]);

  useEffect(() => {
    const status = visiblePost?.reviewStatus || visiblePost?.review_status || visiblePost?.status;
    if (status) setVisibilityStatus(String(status).toLowerCase().includes("hidden") ? "隐藏" : "公开");
  }, [visiblePost?.reviewStatus, visiblePost?.review_status, visiblePost?.status]);
  const repliesByParent = useMemo(() => comments.reduce((groups, item) => {
    if (item.parent) groups[item.parent] = [...(groups[item.parent] || []), item];
    return groups;
  }, {}), [comments]);
  const rootComments = useMemo(() => comments.filter((item) => !item.parent), [comments]);
  const authorIsCurrentUser = Boolean(user?.id && visiblePost?.authorId && String(user.id) === String(visiblePost.authorId));

  useEffect(() => {
    const users = followingQuery.data?.results || followingQuery.data || [];
    if (visiblePost?.authorId) setFollowing(users.some((item) => String(item.id) === String(visiblePost.authorId)));
  }, [followingQuery.data, visiblePost?.authorId]);

  const followMutation = useMutation({
    mutationFn: (nextFollowing) => setUserFollow(visiblePost.authorId, nextFollowing),
    onMutate: (nextFollowing) => setFollowing(nextFollowing),
    onError: (_, nextFollowing) => setFollowing(!nextFollowing),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["users", user?.id, "following"] }),
  });
  async function handleOwnerAction(action) {
    setOwnerMenuOpen(false);
    try {
      if (action === "delete") {
        await deletePost(postId);
        navigate("/profile", { replace: true });
      } else {
        await hidePost(postId, action !== "show");
        setVisibilityStatus(action === "show" ? "公开" : "隐藏");
      }
    } catch (error) {
      setActionError(getApiErrorMessage(error, action === "delete" ? "删除失败" : "隐藏失败"));
    }
  }

  const showPreviousMedia = useCallback(() => {
    setActiveMediaIndex((current) =>
      (current - 1 + detailMediaItems.length) % detailMediaItems.length,
    );
  }, [detailMediaItems.length]);

  const showNextMedia = useCallback(() => {
    setActiveMediaIndex((current) => (current + 1) % detailMediaItems.length);
  }, [detailMediaItems.length]);

  const resetMediaTransform = useCallback(() => {
    setMediaZoom(1);
    setMediaOffset({ x: 0, y: 0 });
    setMediaDragging(false);
    mediaDragRef.current = null;
  }, []);

  const changeMediaZoom = useCallback((change) => {
    setMediaZoom((current) => {
      const next = Math.min(4, Math.max(0.5, Number((current + change).toFixed(2))));
      if (next <= 1) setMediaOffset({ x: 0, y: 0 });
      return next;
    });
  }, []);

  function openMediaViewer() {
    resetMediaTransform();
    setMediaViewerOpen(true);
  }

  function handleViewerPointerDown(event) {
    if (mediaZoom <= 1) return;
    event.preventDefault();
    mediaDragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      originX: mediaOffset.x,
      originY: mediaOffset.y,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
    setMediaDragging(true);
  }

  function handleViewerPointerMove(event) {
    const drag = mediaDragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    setMediaOffset({
      x: drag.originX + event.clientX - drag.startX,
      y: drag.originY + event.clientY - drag.startY,
    });
  }

  function handleViewerPointerEnd(event) {
    if (mediaDragRef.current?.pointerId !== event.pointerId) return;
    mediaDragRef.current = null;
    setMediaDragging(false);
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  }

  function handleMediaKeyDown(event) {
    if (!hasMultipleMedia) return;
    if (event.key === "ArrowLeft") {
      event.preventDefault();
      showPreviousMedia();
    } else if (event.key === "ArrowRight") {
      event.preventDefault();
      showNextMedia();
    }
  }

  function handleMediaTouchStart(event) {
    mediaSwipeStartRef.current = event.touches[0]?.clientX ?? null;
  }

  function handleMediaTouchEnd(event) {
    if (!hasMultipleMedia || mediaSwipeStartRef.current === null) return;
    const endX = event.changedTouches[0]?.clientX;
    const distance = typeof endX === "number" ? endX - mediaSwipeStartRef.current : 0;
    mediaSwipeStartRef.current = null;
    if (Math.abs(distance) < 45) return;
    if (distance > 0) showPreviousMedia();
    else showNextMedia();
  }

  useEffect(() => {
    if (!mediaViewerOpen) return undefined;
    const handleViewerKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        setMediaViewerOpen(false);
      } else if (event.key === "ArrowLeft" && hasMultipleMedia) {
        event.preventDefault();
        showPreviousMedia();
      } else if (event.key === "ArrowRight" && hasMultipleMedia) {
        event.preventDefault();
        showNextMedia();
      }
    };
    document.addEventListener("keydown", handleViewerKeyDown);
    return () => {
      document.removeEventListener("keydown", handleViewerKeyDown);
    };
  }, [hasMultipleMedia, mediaViewerOpen, showNextMedia, showPreviousMedia]);

  useEffect(() => {
    if (mediaViewerOpen) resetMediaTransform();
  }, [activeMediaIndex, mediaViewerOpen, resetMediaTransform]);

  async function toggleLike() {
    if (!visiblePost) return;
    if (IS_API_MODE && !isAuthenticated) {
      navigate("/login", { state: { from: `/posts/${postId}` } });
      return;
    }
    const nextLiked = !visiblePost.liked;
    const previousCaches = queryClient.getQueriesData({ queryKey: ["posts"] });
    await queryClient.cancelQueries({ queryKey: ["posts"] });
    setOptimisticState((state) => ({
      ...state,
      liked: nextLiked,
      likes: visiblePost.likes + (nextLiked ? 1 : -1),
    }));
    updatePostCaches(queryClient, postId, {
      is_liked: nextLiked,
      liked: nextLiked,
      like_count: visiblePost.likes + (nextLiked ? 1 : -1),
    });
    if (!IS_API_MODE) return;
    try {
      await setPostLike(postId, nextLiked);
      setOptimisticState(null);
      await invalidatePostQueries(queryClient);
    } catch (error) {
      restorePostCaches(queryClient, previousCaches);
      setOptimisticState(null);
      setActionError(getApiErrorMessage(error, "点赞操作失败"));
    }
  }

  async function toggleCollection() {
    if (!visiblePost) return;
    if (IS_API_MODE && !isAuthenticated) {
      navigate("/login", { state: { from: `/posts/${postId}` } });
      return;
    }
    const nextSaved = !visiblePost.saved;
    const previousCaches = queryClient.getQueriesData({ queryKey: ["posts"] });
    await queryClient.cancelQueries({ queryKey: ["posts"] });
    setOptimisticState((state) => ({ ...state, saved: nextSaved }));
    updatePostCaches(queryClient, postId, {
      is_collected: nextSaved,
      saved: nextSaved,
    });
    if (!IS_API_MODE) return;
    try {
      await setPostCollection(postId, nextSaved);
      setOptimisticState(null);
      await invalidatePostQueries(queryClient);
    } catch (error) {
      restorePostCaches(queryClient, previousCaches);
      setOptimisticState(null);
      setActionError(getApiErrorMessage(error, "收藏操作失败"));
    }
  }

  async function submitComment(event) {
    event.preventDefault();
    const content = comment.trim();
    if (!content || submittingComment) return;
    if (IS_API_MODE && !isAuthenticated) {
      navigate("/login", { state: { from: `/posts/${postId}` } });
      return;
    }

    setSubmittingComment(true);
    setActionError("");
    const optimisticComment = {
      id: `optimistic-${crypto.randomUUID()}`,
      user_id: user?.id || "me",
      parent: replyTarget?.id || null,
      reply_to_user: replyTarget?.user_id || null,
      content,
      created_at: new Date().toISOString(),
    };
    const previousCaches = queryClient.getQueriesData({ queryKey: ["posts"] });
    await queryClient.cancelQueries({ queryKey: ["posts"] });
    setLocalComments((items) => [...items, optimisticComment]);
    setComment("");
    updatePostCaches(queryClient, postId, {
      comment_count: comments.length + 1,
    });
    try {
      const created = IS_API_MODE
        ? await createPostComment(postId, content, {
            parent: replyTarget?.id,
            replyToUser: replyTarget?.user_id,
          })
        : optimisticComment;
      setLocalComments((items) =>
        items.map((item) => (item.id === optimisticComment.id ? created : item)),
      );
      await invalidatePostQueries(queryClient);
    } catch (error) {
      restorePostCaches(queryClient, previousCaches);
      setLocalComments((items) =>
        items.filter((item) => item.id !== optimisticComment.id),
      );
      setActionError(getApiErrorMessage(error, "评论发布失败"));
    } finally {
      setSubmittingComment(false);
      setReplyTarget(null);
    }
  }

  if (postQuery.isPending && IS_API_MODE) {
    return <div className="detail-state">正在加载作品详情……</div>;
  }

  if (postQuery.isError || !visiblePost) {
    return (
      <div className="detail-state error">
        <p>{getApiErrorMessage(postQuery.error, "作品不存在或暂不可见")}</p>
        <button className="secondary-button" onClick={() => navigate("/")}>
          返回作品流
        </button>
      </div>
    );
  }

  return (
    <section className={`post-detail-page${modal ? " post-detail-modal" : ""}`} role={modal ? "dialog" : undefined} aria-modal={modal || undefined} onClick={modal ? closeDetail : undefined}>
      <button className={`detail-back${modal ? " modal-close" : ""}`} aria-label={modal ? "关闭详情" : "返回作品流"} onClick={closeDetail}>
        {modal ? <X size={24} /> : <><ArrowLeft size={20} />返回</>}
      </button>
      <article className="post-detail-card" onClick={modal ? (event) => event.stopPropagation() : undefined}>
        <div className="detail-card-action">
          {authorIsCurrentUser ? <><button className="icon-button" aria-label="帖子操作" onClick={() => setOwnerMenuOpen((open) => !open)}><MoreHorizontal size={24} /></button>{ownerMenuOpen ? <div className="owner-post-menu"><button onClick={() => handleOwnerAction(visibilityStatus === "隐藏" ? "show" : "hide")}>{visibilityStatus === "隐藏" ? "公开帖子" : "隐藏帖子"}</button><button className="danger" onClick={() => handleOwnerAction("delete")}>删除帖子</button></div> : null}</> : <button className="primary-button" onClick={() => { if (IS_API_MODE && !isAuthenticated) { navigate("/login", { state: { from: `/posts/${postId}` } }); return; } followMutation.mutate(!following); }} disabled={followMutation.isPending}>{followMutation.isPending ? "处理中……" : following ? "已关注" : "关注"}</button>}
        </div>
        <div
          className="detail-artwork"
          tabIndex={hasMultipleMedia ? 0 : undefined}
          aria-label={hasMultipleMedia ? `作品图片，第 ${activeMediaIndex + 1} 张，共 ${detailMediaItems.length} 张` : undefined}
          onKeyDown={handleMediaKeyDown}
          onTouchStart={handleMediaTouchStart}
          onTouchEnd={handleMediaTouchEnd}
        >
          {activeMedia ? (
            <button
              className="detail-media-expand"
              type="button"
              aria-label={`放大查看第 ${activeMediaIndex + 1} 张图片`}
              onClick={openMediaViewer}
            >
              <img
                key={activeMedia.id || activeMedia.url}
                src={activeMedia.url}
                alt={`${visiblePost.author}发布的设计作品，第 ${activeMediaIndex + 1} 张`}
              />
            </button>
          ) : (
            <PosterArtwork artwork={visiblePost.artwork} />
          )}
          {hasMultipleMedia ? (
            <>
              <button
                className="detail-media-control previous"
                type="button"
                aria-label="上一张图片"
                onClick={showPreviousMedia}
              >
                <ChevronLeft size={24} />
              </button>
              <button
                className="detail-media-control next"
                type="button"
                aria-label="下一张图片"
                onClick={showNextMedia}
              >
                <ChevronRight size={24} />
              </button>
              <span className="detail-media-count">
                {activeMediaIndex + 1}/{detailMediaItems.length}
              </span>
              <div className="detail-media-dots" aria-label="选择作品图片">
                {detailMediaItems.map((item, index) => (
                  <button
                    key={item.id || item.url}
                    className={index === activeMediaIndex ? "active" : ""}
                    type="button"
                    aria-label={`查看第 ${index + 1} 张图片`}
                    aria-current={index === activeMediaIndex ? "true" : undefined}
                    onClick={() => setActiveMediaIndex(index)}
                  />
                ))}
              </div>
            </>
          ) : null}
        </div>

        <div className="detail-content">
          <header className="detail-author">
            <img className="avatar avatar-md" src={visiblePost.avatar} alt="" />
            <div>
              <strong>{visiblePost.author}</strong>
              <span>@{visiblePost.username}</span>
            </div>
          </header>

          <div className="detail-copy">
            {visiblePost.location ? <span className="eyebrow">{visiblePost.location}</span> : null}
            <h1>{visiblePost.caption}</h1>
            <p>{visiblePost.content}</p>
            <div className="post-tags">
              {visiblePost.tags.map((tag) => (
                <span key={tag}>#{tag}</span>
              ))}
            </div>
          </div>

          <div className="detail-metrics">
            <button className={visiblePost.liked ? "liked" : ""} onClick={toggleLike}>
              <Heart size={22} fill={visiblePost.liked ? "currentColor" : "none"} />
              {visiblePost.likes}
            </button>
            <button type="button" className="detail-comment-metric" onClick={() => { setReplyTarget(null); commentInputRef.current?.focus(); }} aria-label="发表评论">
              <MessageCircle size={21} />
              {comments.length}
            </button>
            <span>
              <Eye size={21} />
              {visiblePost.views}
            </span>
            <button className="save-action" onClick={toggleCollection}>
              <Bookmark size={22} fill={visiblePost.saved ? "currentColor" : "none"} />
            </button>
          </div>

          {actionError && <div className="form-error">{actionError}</div>}

          <div className="detail-comments">
            <h2>评论</h2>
            {commentsQuery.isPending && IS_API_MODE && (
              <p className="comments-hint">正在加载评论……</p>
            )}
            {rootComments.map((item) => (
              <div key={item.id}>
              <article>
                <img className="comment-avatar" src={commentUsers[item.user_id]?.avatar_url || defaultAvatar} alt="" />
                <div>
                  <strong>{commentUsers[item.user_id]?.nickname || commentUsers[item.user_id]?.username || item.username || `用户 ${item.user_id}`}</strong>
                  <p>{item.content}</p>
                  <small>{formatCommentTime(item.created_at)}</small>
                  <button className="comment-reply" type="button" onClick={() => setReplyTarget(item)}>回复</button>
                </div>
              </article>
              {repliesByParent[item.id]?.length ? (
                <>
                  <button className="replies-toggle" type="button" onClick={() => setExpandedReplies((current) => ({ ...current, [item.id]: !current[item.id] }))}>
                    {expandedReplies[item.id] ? "收起回复" : `查看 ${repliesByParent[item.id].length} 条回复`}
                  </button>
                  {expandedReplies[item.id] ? repliesByParent[item.id].map((reply) => (
                    <article className="comment-reply-item" key={reply.id}>
                      <img className="comment-avatar" src={commentUsers[reply.user_id]?.avatar_url || defaultAvatar} alt="" />
                      <div>
                        <strong>{commentUsers[reply.user_id]?.nickname || commentUsers[reply.user_id]?.username || `用户 ${reply.user_id}`}</strong>
                        <p>{reply.content}</p>
                        <small>{formatCommentTime(reply.created_at)}</small>
                        <button className="comment-reply" type="button" onClick={() => setReplyTarget(reply)}>回复</button>
                      </div>
                    </article>
                  )) : null}
                </>
              ) : null}
              </div>
            ))}
            {!commentsQuery.isPending && comments.length === 0 && (
              <p className="comments-hint">还没有评论，来说说你的设计感受。</p>
            )}
            {commentsQuery.hasNextPage && (
              <button
                className="load-comments"
                disabled={commentsQuery.isFetchingNextPage}
                onClick={() => commentsQuery.fetchNextPage()}
              >
                {commentsQuery.isFetchingNextPage ? "加载中……" : "查看更多评论"}
              </button>
            )}
          </div>

          <form className="detail-comment-form" onSubmit={submitComment}>
            {replyTarget ? <div className="reply-hint">回复 {commentUsers[replyTarget.user_id]?.nickname || `用户 ${replyTarget.user_id}`}</div> : null}
            <input
              ref={commentInputRef}
              value={comment}
              onChange={(event) => setComment(event.target.value)}
              placeholder={replyTarget ? "写下回复内容……" : "写下你的设计反馈……"}
              aria-label="发表评论"
            />
            <button disabled={!comment.trim() || submittingComment} aria-label="发送评论">
              <Send size={20} />
            </button>
          </form>
        </div>
      </article>
      {mediaViewerOpen && activeMedia ? (
        <div
          className="detail-media-viewer"
          role="dialog"
          aria-modal="true"
          aria-label={`放大查看作品图片，第 ${activeMediaIndex + 1} 张，共 ${detailMediaItems.length} 张`}
          onWheel={(event) => {
            event.preventDefault();
            changeMediaZoom(event.deltaY < 0 ? 0.2 : -0.2);
          }}
          onClick={(event) => {
            event.stopPropagation();
            setMediaViewerOpen(false);
          }}
        >
          <button
            className="detail-media-viewer-close"
            type="button"
            aria-label="关闭图片预览"
            onClick={() => setMediaViewerOpen(false)}
          >
            <X size={27} />
          </button>
          {hasMultipleMedia ? (
            <>
              <button
                className="detail-media-viewer-control previous"
                type="button"
                aria-label="上一张图片"
                onClick={(event) => {
                  event.stopPropagation();
                  showPreviousMedia();
                }}
              >
                <ChevronLeft size={30} />
              </button>
              <button
                className="detail-media-viewer-control next"
                type="button"
                aria-label="下一张图片"
                onClick={(event) => {
                  event.stopPropagation();
                  showNextMedia();
                }}
              >
                <ChevronRight size={30} />
              </button>
              <span className="detail-media-viewer-count">
                {activeMediaIndex + 1}/{detailMediaItems.length}
              </span>
            </>
          ) : null}
          <div
            className="detail-media-viewer-toolbar"
            aria-label="图片缩放控制"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              aria-label="缩小图片"
              disabled={mediaZoom <= 0.5}
              onClick={() => changeMediaZoom(-0.25)}
            >
              <Minus size={20} />
            </button>
            <button
              className="detail-media-zoom-value"
              type="button"
              aria-label="恢复图片适应屏幕"
              onClick={resetMediaTransform}
            >
              {Math.round(mediaZoom * 100)}%
            </button>
            <button
              type="button"
              aria-label="放大图片"
              disabled={mediaZoom >= 4}
              onClick={() => changeMediaZoom(0.25)}
            >
              <Plus size={20} />
            </button>
            <button
              type="button"
              aria-label="重置图片位置和比例"
              onClick={resetMediaTransform}
            >
              <RotateCcw size={18} />
            </button>
          </div>
          <img
            key={`viewer-${activeMedia.id || activeMedia.url}`}
            className={mediaDragging ? "dragging" : ""}
            src={activeMedia.url}
            alt={`${visiblePost.author}发布的设计作品，第 ${activeMediaIndex + 1} 张大图`}
            draggable="false"
            style={{
              transform: `translate3d(${mediaOffset.x}px, ${mediaOffset.y}px, 0) scale(${mediaZoom})`,
            }}
            onDoubleClick={() => {
              if (mediaZoom > 1) resetMediaTransform();
              else changeMediaZoom(1);
            }}
            onPointerDown={handleViewerPointerDown}
            onPointerMove={handleViewerPointerMove}
            onPointerUp={handleViewerPointerEnd}
            onPointerCancel={handleViewerPointerEnd}
            onClick={(event) => event.stopPropagation()}
          />
        </div>
      ) : null}
    </section>
  );
}
